import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Archive, RotateCcw, Layers } from "lucide-react";
import { PageHeader, Btn, Field, inputCls, Modal, Card } from "@/components/cc";

export const VERDICT_COLOR = {
  unverified: "text-zinc-400", true: "text-emerald-400", mostly_true: "text-emerald-300",
  misleading: "text-amber-400", mostly_false: "text-orange-400", false: "text-red-400",
  unsubstantiated: "text-fuchsia-400", out_of_context: "text-sky-400",
};
const STATUS_COLOR = {
  new: "text-sky-400", researching: "text-amber-400", evidence_review: "text-violet-400",
  resolved: "text-emerald-400", closed: "text-zinc-500",
};

function Stat({ label, value, color, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className={`font-tactical font-black text-2xl ${color || "text-white"}`}>{value ?? 0}</div>
      <div className="label-caps text-zinc-500 mt-1">{label}</div>
    </Card>
  );
}

export default function ClaimTracker() {
  const nav = useNavigate();
  const { user } = useAuth();
  const canManage = (user?.roles || []).some((r) => ["editor", "super_admin"].includes(r));
  const [meta, setMeta] = useState(null);
  const [dash, setDash] = useState(null);
  const [entries, setEntries] = useState([]);
  const [groups, setGroups] = useState([]);
  const [filters, setFilters] = useState({ q: "", speaker: "", party: "", institution: "", topic: "", verdict: "", status: "", region: "", date_from: "", date_to: "" });
  const [tab, setTab] = useState("active");
  const [open, setOpen] = useState(false);
  const [groupModal, setGroupModal] = useState(false);
  const [groupTitle, setGroupTitle] = useState("");
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);
  const [action, setAction] = useState(null); // {mode, entry}
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  function blank() {
    return { speaker: "", speaker_role: "", party: "", institution: "", claim_text: "", claim_summary: "", date_made: "", location: "", topic: "", region: "", group_id: "" };
  }

  const loadMeta = () => {
    api.get("/claim-tracker/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/claim-tracker/dashboard").then((r) => setDash(r.data)).catch(() => {});
    api.get("/claim-tracker/groups").then((r) => setGroups(r.data)).catch(() => {});
  };
  const loadEntries = useCallback(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    if (tab === "archived") params.archived = true;
    setLoading(true); setEntries([]);
    api.get("/claim-tracker/entries", { params }).then((r) => setEntries(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [filters, tab]);

  useEffect(() => { loadMeta(); }, []);
  useEffect(() => { loadEntries(); }, [loadEntries]);

  const create = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.group_id) delete payload.group_id;
      const { data } = await api.post("/claim-tracker/entries", payload);
      toast.success("Claim entry created");
      setOpen(false); setForm(blank()); loadMeta();
      nav(`/command-center/claim-tracker/${data.id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const createGroup = async (e) => {
    e.preventDefault();
    try {
      await api.post("/claim-tracker/groups", { title: groupTitle });
      toast.success("Group created");
      setGroupModal(false); setGroupTitle(""); loadMeta();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const runAction = async () => {
    if (busy || !action) return;
    setBusy(true);
    try {
      if (action.mode === "archive") await api.delete(`/claim-tracker/entries/${action.entry.id}`);
      else await api.post(`/claim-tracker/entries/${action.entry.id}/restore`);
      toast.success(action.mode === "archive" ? "Entry archived" : "Entry restored");
      setAction(null); loadEntries(); loadMeta();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const vlabel = (v) => meta?.verdict_labels?.[v] || v;

  return (
    <div className="p-6">
      <PageHeader title="Political Claim Tracker" subtitle="Track public/political claims, evidence and verdicts over time — private workspace.">
        <Btn variant="ghost" data-testid="ct-group-btn" onClick={() => setGroupModal(true)}><Layers className="h-4 w-4" /> New Group</Btn>
        <Btn data-testid="ct-new-entry-btn" disabled={!meta} onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> New Claim</Btn>
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-4">
        <Stat label="Total" value={dash?.total} testid="ct-stat-total" />
        <Stat label="Unverified" value={dash?.by_verdict?.unverified} color="text-zinc-300" testid="ct-stat-unverified" />
        <Stat label="True" value={dash?.by_verdict?.true} color="text-emerald-400" testid="ct-stat-true" />
        <Stat label="Misleading" value={dash?.by_verdict?.misleading} color="text-amber-400" testid="ct-stat-misleading" />
        <Stat label="False" value={dash?.by_verdict?.false} color="text-red-400" testid="ct-stat-false" />
        <Stat label="Out of Context" value={dash?.by_verdict?.out_of_context} color="text-sky-400" testid="ct-stat-ooc" />
        <Stat label="In Review" value={(dash?.by_status?.researching || 0) + (dash?.by_status?.evidence_review || 0)} color="text-violet-400" testid="ct-stat-review" />
      </div>

      {(dash?.by_party?.length > 0 || dash?.by_topic?.length > 0) && (
        <div className="grid md:grid-cols-2 gap-3 mb-6">
          <Card className="p-4" data-testid="ct-by-party">
            <div className="label-caps text-zinc-500 mb-2">Top Parties / Institutions</div>
            <div className="flex flex-wrap gap-2">
              {[...(dash.by_party || []), ...(dash.by_institution || [])].slice(0, 8).map((x, i) => (
                <span key={i} className="text-xs px-2 py-1 rounded-sm border border-zinc-700 text-zinc-300">{x.key} <span className="text-zinc-500">· {x.count}</span></span>
              ))}
              {(dash.by_party?.length || 0) + (dash.by_institution?.length || 0) === 0 && <span className="text-xs text-zinc-600">No data yet.</span>}
            </div>
          </Card>
          <Card className="p-4" data-testid="ct-by-topic">
            <div className="label-caps text-zinc-500 mb-2">Top Topics</div>
            <div className="flex flex-wrap gap-2">
              {(dash.by_topic || []).map((x, i) => (
                <span key={i} className="text-xs px-2 py-1 rounded-sm border border-zinc-700 text-zinc-300">{x.key} <span className="text-zinc-500">· {x.count}</span></span>
              ))}
              {(dash.by_topic?.length || 0) === 0 && <span className="text-xs text-zinc-600">No data yet.</span>}
            </div>
          </Card>
        </div>
      )}

      <Card className="p-4 mb-6">
        <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Field label="Search"><input data-testid="ct-filter-q" className={inputCls} value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Claim, speaker, party" /></Field>
          <Field label="Speaker"><input data-testid="ct-filter-speaker" className={inputCls} value={filters.speaker} onChange={(e) => setFilters({ ...filters, speaker: e.target.value })} /></Field>
          <Field label="Verdict"><select data-testid="ct-filter-verdict" className={inputCls} value={filters.verdict} onChange={(e) => setFilters({ ...filters, verdict: e.target.value })}><option value="">All</option>{(meta?.verdicts || []).map((v) => <option key={v} value={v}>{vlabel(v)}</option>)}</select></Field>
          <Field label="Status"><select data-testid="ct-filter-status" className={inputCls} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}><option value="">All</option>{(meta?.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
          <Field label="Topic"><select data-testid="ct-filter-topic" className={inputCls} value={filters.topic} onChange={(e) => setFilters({ ...filters, topic: e.target.value })}><option value="">All</option>{(meta?.topics || []).map((t) => <option key={t} value={t}>{t}</option>)}</select></Field>
          <Field label="Region"><select data-testid="ct-filter-region" className={inputCls} value={filters.region} onChange={(e) => setFilters({ ...filters, region: e.target.value })}><option value="">All</option>{(meta?.regions || []).map((r) => <option key={r} value={r}>{r}</option>)}</select></Field>
          <Field label="Party"><input data-testid="ct-filter-party" className={inputCls} value={filters.party} onChange={(e) => setFilters({ ...filters, party: e.target.value })} /></Field>
          <Field label="Institution"><input data-testid="ct-filter-institution" className={inputCls} value={filters.institution} onChange={(e) => setFilters({ ...filters, institution: e.target.value })} /></Field>
          <Field label="From"><input type="date" data-testid="ct-filter-from" className={inputCls} value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} /></Field>
          <Field label="To"><input type="date" data-testid="ct-filter-to" className={inputCls} value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} /></Field>
        </div>
      </Card>

      <div className="flex items-center gap-4 border-b border-zinc-800 mb-4">
        {["active", "archived"].map((t) => (
          <button key={t} data-testid={`ct-tab-${t}`} onClick={() => setTab(t)}
            className={`px-3 py-1.5 text-sm font-semibold border-b-2 transition-colors capitalize ${tab === t ? "border-emerald-400 text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>{t}</button>
        ))}
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full min-w-[920px] text-sm" data-testid="ct-entries-table">
          <thead>
            <tr className="text-left label-caps text-zinc-500 border-b border-zinc-800">
              <th className="px-4 py-2.5">Speaker</th><th className="px-4 py-2.5">Claim</th>
              <th className="px-4 py-2.5">Party / Inst.</th><th className="px-4 py-2.5">Topic</th>
              <th className="px-4 py-2.5">Verdict</th><th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">{tab === "archived" ? "Archived" : "Date"}</th>
              {canManage && <th className="px-4 py-2.5">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-900">
            {loading && <tr><td colSpan={canManage ? 8 : 7} className="px-4 py-6 text-zinc-600" data-testid="ct-loading">Loading…</td></tr>}
            {!loading && entries.length === 0 && <tr><td colSpan={canManage ? 8 : 7} className="px-4 py-6 text-zinc-600" data-testid="ct-empty">{tab === "archived" ? "No archived entries." : "No claim entries match."}</td></tr>}
            {!loading && entries.map((e) => (
              <tr key={e.id} className={`hover:bg-zinc-900/50 ${tab === "active" ? "cursor-pointer" : ""}`} data-testid={`ct-row-${e.id}`} onClick={() => tab === "active" && nav(`/command-center/claim-tracker/${e.id}`)}>
                <td className="px-4 py-2.5"><div className="font-medium">{e.speaker}</div>{e.speaker_role && <div className="text-xs text-zinc-500">{e.speaker_role}</div>}</td>
                <td className="px-4 py-2.5 text-zinc-300 max-w-sm"><div className="truncate">{e.claim_summary || e.claim_text}</div>{e.group_id && <span className="text-[0.65rem] text-fuchsia-400">grouped</span>}</td>
                <td className="px-4 py-2.5 text-zinc-400 text-xs">{[e.party, e.institution].filter(Boolean).join(" / ") || "—"}</td>
                <td className="px-4 py-2.5 text-zinc-400 text-xs">{e.topic || "—"}</td>
                <td className={`px-4 py-2.5 font-mono-data uppercase text-xs ${VERDICT_COLOR[e.verdict] || "text-zinc-400"}`}>{vlabel(e.verdict)}</td>
                <td className={`px-4 py-2.5 font-mono-data uppercase text-xs ${STATUS_COLOR[e.status] || ""}`}>{e.status}</td>
                <td className="px-4 py-2.5 text-zinc-600 text-xs">{(tab === "archived" ? e.deleted_at : e.date_made) ? new Date(tab === "archived" ? e.deleted_at : e.date_made).toLocaleDateString() : "—"}</td>
                {canManage && (
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    {tab === "active" ? (
                      <button data-testid={`ct-archive-${e.id}`} onClick={(ev) => { ev.stopPropagation(); setAction({ mode: "archive", entry: e }); }}
                        className="inline-flex items-center gap-1.5 rounded-sm border border-red-500/40 bg-red-500/10 px-2.5 py-1 text-xs font-medium text-red-300 hover:bg-red-500/20 transition-colors"><Archive className="h-3.5 w-3.5" /> Archive</button>
                    ) : (
                      <button data-testid={`ct-restore-${e.id}`} onClick={(ev) => { ev.stopPropagation(); setAction({ mode: "restore", entry: e }); }}
                        className="inline-flex items-center gap-1.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-300 hover:bg-emerald-500/20 transition-colors"><RotateCcw className="h-3.5 w-3.5" /> Restore</button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Modal open={!!action} onClose={() => { if (!busy) setAction(null); }} title={action?.mode === "archive" ? "Archive claim entry" : "Restore claim entry"}>
        {action && (
          <div className="space-y-4" data-testid="ct-action-confirm">
            <p className="text-sm text-zinc-400">{action.mode === "archive" ? "Archive this entry? It is hidden from the active list but preserved with all evidence links and history, and can be restored anytime." : "Restore this entry to the active list? All links and history are preserved."}</p>
            <div className="text-sm font-mono-data break-words" data-testid="ct-action-title">{action.entry.speaker}: {action.entry.claim_summary || action.entry.claim_text}</div>
            <div className="flex gap-2">
              <Btn variant="ghost" data-testid="ct-action-cancel" disabled={busy} onClick={() => setAction(null)}>Cancel</Btn>
              <Btn data-testid="ct-action-confirm-btn" disabled={busy} onClick={runAction} className="flex-1 justify-center">{busy ? "Working…" : action.mode === "archive" ? "Archive" : "Restore"}</Btn>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={groupModal} onClose={() => setGroupModal(false)} title="New Claim Group">
        <form onSubmit={createGroup} className="space-y-4">
          <Field label="Group title"><input data-testid="ct-group-title" className={inputCls} required value={groupTitle} onChange={(e) => setGroupTitle(e.target.value)} placeholder="e.g. 'Debt-to-GDP claims'" /></Field>
          <p className="text-xs text-zinc-600">Groups associate related or repeated claims. Underlying evidence records are never merged.</p>
          <Btn data-testid="ct-group-save" type="submit" className="w-full justify-center">Create Group</Btn>
        </form>
      </Modal>

      <Modal open={open} onClose={() => setOpen(false)} title="New Political Claim" wide>
        <form onSubmit={create} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Speaker / Person"><input data-testid="ct-speaker" className={inputCls} required value={form.speaker} onChange={(e) => setForm({ ...form, speaker: e.target.value })} /></Field>
            <Field label="Role / Title"><input data-testid="ct-role" className={inputCls} value={form.speaker_role} onChange={(e) => setForm({ ...form, speaker_role: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Political Party"><input data-testid="ct-party" className={inputCls} value={form.party} onChange={(e) => setForm({ ...form, party: e.target.value })} /></Field>
            <Field label="Institution"><input data-testid="ct-institution" className={inputCls} value={form.institution} onChange={(e) => setForm({ ...form, institution: e.target.value })} /></Field>
          </div>
          <Field label="Exact claim text"><textarea data-testid="ct-claim-text" className={inputCls} rows={3} required value={form.claim_text} onChange={(e) => setForm({ ...form, claim_text: e.target.value })} /></Field>
          <Field label="Normalized summary"><textarea data-testid="ct-claim-summary" className={inputCls} rows={2} value={form.claim_summary} onChange={(e) => setForm({ ...form, claim_summary: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date made"><input type="date" data-testid="ct-date" className={inputCls} value={form.date_made} onChange={(e) => setForm({ ...form, date_made: e.target.value })} /></Field>
            <Field label="Location / Platform"><input data-testid="ct-location" className={inputCls} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Parliament, Twitter/X, rally" /></Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Topic"><select data-testid="ct-topic" className={inputCls} value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })}><option value="">—</option>{(meta?.topics || []).map((t) => <option key={t} value={t}>{t}</option>)}</select></Field>
            <Field label="Region"><select data-testid="ct-region" className={inputCls} value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}><option value="">—</option>{(meta?.regions || []).map((r) => <option key={r} value={r}>{r}</option>)}</select></Field>
            <Field label="Group (optional)"><select data-testid="ct-group-select" className={inputCls} value={form.group_id} onChange={(e) => setForm({ ...form, group_id: e.target.value })}><option value="">None</option>{groups.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}</select></Field>
          </div>
          <Btn data-testid="ct-save-btn" type="submit" disabled={saving} className="w-full justify-center">{saving ? "Creating…" : "Create Claim"}</Btn>
        </form>
      </Modal>
    </div>
  );
}
